/**
 * Link cliccabili nel testo semplice dei messaggi del supporto (thread degli
 * operatori, vista del richiedente, pagina ospite, bozze). Solo http(s) e
 * indirizzi che iniziano con «www.» (aperti in https); niente HTML: il testo
 * resta testo e i link sono elementi React, quindi nessuna iniezione.
 * La punteggiatura finale («.», «,», «)» senza la sua «(»…) resta fuori dal link.
 */
import React from 'react';

export interface TextPart {
  text: string;
  href?: string;
}

// Si ferma a spazi (anche a larghezza zero), < > " e alle virgolette caporali e tipografiche.
const URL_RE = /\b(?:https?:\/\/|www\.)[^\s<>"«»“”‘’​-‍﻿]+/gi;
const TRAILING = /[.,;:!?']+$/;

/** Toglie la punteggiatura finale e le parentesi di chiusura senza apertura. */
const trimUrl = (raw: string): string => {
  let url = raw;
  for (;;) {
    const before = url;
    url = url.replace(TRAILING, '');
    for (const [open, close] of [['(', ')'], ['[', ']'], ['{', '}']]) {
      if (url.endsWith(close) && url.split(open).length < url.split(close).length) url = url.slice(0, -1);
    }
    if (url === before) return url;
  }
};

/** Divide il testo in parti, con `href` sulle parti che sono link. */
export const splitLinks = (text: string): TextPart[] => {
  const parts: TextPart[] = [];
  let last = 0;
  const re = new RegExp(URL_RE.source, URL_RE.flags);
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const start = m.index;
    const url = trimUrl(m[0]);
    // Serve almeno un carattere dopo lo schema o dopo «www.» (anche dopo aver
    // tolto la punteggiatura: «www..» non e un link), e mai dentro un'email.
    if (!/^(?:https?:\/\/|www\.)./i.test(url) || text[start - 1] === '@') continue;
    if (start > last) parts.push({ text: text.slice(last, start) });
    parts.push({ text: url, href: /^www\./i.test(url) ? `https://${url}` : url });
    last = start + url.length;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts.length ? parts : [{ text }];
};

const LINK_STYLE: React.CSSProperties = {
  color: 'inherit',
  textDecoration: 'underline',
  textUnderlineOffset: 2,
  overflowWrap: 'anywhere',
};

/** Testo con i link cliccabili (nuova scheda, senza referrer). */
export const Linkified: React.FC<{ text: string | null | undefined }> = ({ text }) => {
  if (!text) return null;
  return (
    <>
      {splitLinks(text).map((p, i) =>
        p.href ? (
          <a
            key={i}
            href={p.href}
            target="_blank"
            rel="noopener noreferrer nofollow"
            style={LINK_STYLE}
            onClick={(e) => e.stopPropagation()}
          >
            {p.text}
          </a>
        ) : (
          <React.Fragment key={i}>{p.text}</React.Fragment>
        )
      )}
    </>
  );
};
